import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, TableProperties } from "lucide-react";

const fmtCell = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
};

export function KpiDrillDown({ open, onClose, buId, month, kpi }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !kpi?.code || !buId || !month) return;
    setBusy(true); setData(null); setError(null);
    api.get(`/qms/kpi-drilldown?business_unit_id=${buId}&month=${month}&kpi_code=${kpi.code}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load drill-down"))
      .finally(() => setBusy(false));
  }, [open, kpi?.code, buId, month]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="kpi-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <TableProperties className="w-4 h-4 text-slate-500" />
            {data?.title || kpi?.name || "KPI drill-down"}
            <span className="text-xs font-normal text-slate-500">
              · {data?.business_unit?.code}{month ? ` · ${month}` : ""}
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Raw data records behind the selected KPI score
          </DialogDescription>
        </DialogHeader>
        {busy && (
          <div className="flex items-center justify-center py-10 text-slate-400 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading data…
          </div>
        )}
        {error && (
          <div data-testid="kpi-drilldown-error"
               className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        {data && !busy && (
          <div className="flex-1 overflow-hidden flex flex-col gap-3">
            {Object.keys(data.summary || {}).length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="kpi-drilldown-summary">
                {Object.entries(data.summary).map(([k, v]) => (
                  <div key={k} className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div>
                    <div className="text-sm font-semibold text-slate-900 tabular-nums">{fmtCell(v)}</div>
                  </div>
                ))}
              </div>
            )}
            {data.note && (
              <div className="text-[11px] text-slate-500 italic">{data.note}</div>
            )}
            <div className="flex-1 overflow-auto border border-slate-200 rounded-lg">
              <table className="w-full text-sm" data-testid="kpi-drilldown-table">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    {(data.columns || []).map((c) => (
                      <th key={c.key} className="text-left font-medium px-3 py-2 whitespace-nowrap">{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(data.rows || []).length === 0 ? (
                    <tr>
                      <td colSpan={(data.columns || []).length || 1}
                          className="text-center py-8 text-slate-400 text-sm">
                        No underlying records for this month.
                      </td>
                    </tr>
                  ) : data.rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      {(data.columns || []).map((c) => (
                        <td key={c.key} className="px-3 py-1.5 tabular-nums text-slate-700 whitespace-nowrap max-w-[320px] truncate">
                          {fmtCell(r[c.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.row_count > (data.rows || []).length && (
              <div className="text-[11px] text-slate-400">
                Showing first {data.rows.length} of {data.row_count} records.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
